/**
 * @file request-fields.tsx
 * @module app/modules/service-request/components/fields/request-fields
 * @description Renders, starts, and checks an action's inputs from its field definitions, using
 * the kind registry. The form and the request list never need to know which kinds exist.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import {
  ServiceRequestActionDefinition,
  ServiceRequestField,
  ServiceRequestPayload,
} from '../../types/service-request.types';
import { getFieldKind } from './field-registry';

const UNSUPPORTED_KIND =
  'This input is not supported by this version of the page. Refresh the page and try again.';

export type FieldErrors = Record<string, string>;

const fieldId = (field: ServiceRequestField) => `sr-field-${field.name}`;

/** Starting values for a new request: each field's empty value. */
export const emptyValuesFor = (
  action: ServiceRequestActionDefinition
): ServiceRequestPayload =>
  Object.fromEntries(
    action.fields.map((field) => [
      field.name,
      getFieldKind(field.kind)?.emptyValue(),
    ])
  );

/** Required and kind-specific checks. An empty result means the values are valid. */
export const validateFields = (
  action: ServiceRequestActionDefinition,
  values: ServiceRequestPayload
): FieldErrors => {
  const errors: FieldErrors = {};
  action.fields.forEach((field) => {
    const kind = getFieldKind(field.kind);
    if (!kind) {
      errors[field.name] = UNSUPPORTED_KIND;
      return;
    }
    const value = values[field.name];
    if (field.required && kind.isEmpty(value)) {
      errors[field.name] = `${field.label} is required.`;
      return;
    }
    const message = kind.validate?.(value, field);
    if (message) errors[field.name] = message;
  });
  return errors;
};

type RequestFieldsEditorProps = {
  action: ServiceRequestActionDefinition;
  values: ServiceRequestPayload;
  errors: FieldErrors;
  disabled: boolean;
  onChange: (name: string, value: unknown) => void;
};

/** One labelled input per field, in the order the server lists them. */
export const RequestFieldsEditor = ({
  action,
  values,
  errors,
  disabled,
  onChange,
}: RequestFieldsEditorProps) => (
  <>
    {action.fields.map((field) => {
      const id = fieldId(field);
      const kind = getFieldKind(field.kind);
      const error = errors[field.name] || (kind ? '' : UNSUPPORTED_KIND);
      let describedBy: string | undefined;
      if (error) describedBy = `${id}-error`;
      else if (field.helpText) describedBy = `${id}-help`;
      const Editor = kind?.Editor;

      return (
        <div key={field.name} className="sr-form__section">
          <label className="sr-form__label" htmlFor={id}>
            {field.label}
            {field.required ? <span className="text-danger"> *</span> : null}
          </label>
          {Editor ? (
            <Editor
              id={id}
              field={field}
              value={values[field.name]}
              onChange={(value) => onChange(field.name, value)}
              invalid={Boolean(error)}
              disabled={disabled}
              describedBy={describedBy}
            />
          ) : null}
          {error ? (
            <div id={`${id}-error`} className="sr-field-error">
              {error}
            </div>
          ) : null}
          {!error && field.helpText ? (
            <div id={`${id}-help`} className="sr-field-hint">
              {field.helpText}
            </div>
          ) : null}
        </div>
      );
    })}
  </>
);

const toText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

/** Fallback for payload values whose action or kind this page does not know. */
const PlainValue = ({ value }: { value: unknown }) => {
  if (Array.isArray(value)) {
    return (
      <div className="sr-chip-list">
        {value.map((item, index) => (
          <span key={index} className="sr-chip sr-chip--static">
            {toText(item)}
          </span>
        ))}
      </div>
    );
  }
  const text = toText(value);
  return text ? <span>{text}</span> : <span className="sr-muted">-</span>;
};

type RequestFieldsDisplayProps = {
  payload: ServiceRequestPayload;
  /** The action's fields when its definition is known; otherwise the payload is shown as-is. */
  fields?: ServiceRequestField[];
  /** Label a single input too, for layouts that have no column header naming it. */
  alwaysLabel?: boolean;
};

/** Read-only view of a request's inputs. By default, labels show only when there is more than one input. */
export const RequestFieldsDisplay = ({
  payload,
  fields,
  alwaysLabel,
}: RequestFieldsDisplayProps) => {
  const entries = fields
    ? fields.map((field) => ({ name: field.name, label: field.label, field }))
    : Object.keys(payload).map((name) => ({
        name,
        label: name,
        field: undefined,
      }));
  const showLabels = alwaysLabel || entries.length > 1;

  return (
    <div className="sr-fields">
      {entries.map(({ name, label, field }) => {
        const kind = field ? getFieldKind(field.kind) : undefined;
        const Display = kind?.Display;
        return (
          <div key={name} className="sr-fields__item">
            {showLabels ? (
              <div className="sr-muted sr-fields__label">{label}</div>
            ) : null}
            {Display && field && kind ? (
              <Display field={field} value={kind.fromPayload(payload[name])} />
            ) : (
              <PlainValue value={payload[name]} />
            )}
          </div>
        );
      })}
    </div>
  );
};
